import { useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import {
  Button,
  Table,
  SearchPanel,
  Badge,
  FormItem,
  Form,
  Input,
  LabelDate,
} from '../../../../../components';
import AddIcon from '@mui/icons-material/Add';
import { SearchIcon } from '../../../../../components/SearchBar/SearchIcon';
import { useCallsManagement } from '../../hooks/useCallsManagement';
import { CallsTaskDetailsPanel } from '..';
import { FILTER_OPTIONS, statusConfig } from '../../utils/const';
import type { CallStatus } from '../../utils/const';
import { AudioPlayButton } from './AudioPlayButton';
import { FilterButtons } from './FilterButtons';
import type { CallTaskDetail } from '../../../../../api';

export const CallsTasksList = () => {
  const { params } = useRouteMatch<{ task_id: string }>();
  const history = useHistory();

  const {
    loading,
    sort,
    setSort,
    filters,
    setPage,
    setFilters,
    pagination,
    data,
  } = useCallsManagement(params.task_id || '');
  const [selectedCall, setSelectedCall] = useState<CallTaskDetail | null>(null);

  return (
    <div className="p-4 relative">
      <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
        <div>
          <FilterButtons
            filters={FILTER_OPTIONS}
            onChange={selectedFilters => {
              console.log('Selected filters:', selectedFilters);
            }}
          />
        </div>
        <div className="flex flex-wrap items-center mb-4 gap-4">
          <div className="flex-1 min-w-[200px]">
            <Form initialValue={filters ?? {}} onChange={setFilters}>
              <FormItem inline={false} name="name" label="">
                <Input
                  placeholder="Search"
                  inputClassName="pl-20"
                  prefix={<SearchIcon />}
                />
              </FormItem>
            </Form>
          </div>
          <div className="shrink-0">
            <Button
              label="Add New"
              icon={<AddIcon className="text-inherit" />}
              onClick={() => {
                history.push(`/batch_calls/task_wizard`);
              }}
              size="medium"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        <div className={selectedCall ? 'w-2/3' : 'w-full'}>
          <div className="overflow-x-auto">
            <Table
              sort={sort}
              onSort={setSort}
              data={data}
              loading={loading}
              isCustom
              headersClassName="text-secondary font-bold text-tiny"
              cellsContentClassName="text-mainText text-tiny"
              emptyMessage="There are no calls."
              config={{
                columns: [
                  { header: 'Contact Name', getValue: row => row.contactName },
                  { header: 'Phone Number', getValue: row => row.phoneNumber },
                  {
                    header: 'Date & Time',
                    getValue: row => (
                      <LabelDate
                        date={row.dateTime}
                        showTime
                        formatDate="dd/MMM/yyyy"
                      />
                    ),
                  },
                  {
                    header: 'Retry At',
                    getValue: row => <div>{row.retryAt}</div>,
                  },
                  { header: 'Duration', getValue: row => row.duration },
                  {
                    header: 'Voice Mail Left',
                    getValue: row => row.voiceMailLeft,
                  },
                  {
                    header: 'Status',
                    getValue: row => (
                      <Badge
                        config={statusConfig}
                        status={row.status as CallStatus}
                        showIconOnly={!!selectedCall}
                      />
                    ),
                  },
                  {
                    header: 'Recordings',
                    getValue: (row: CallTaskDetail) => (
                      <AudioPlayButton isCompact={!!selectedCall} />
                    ),
                  },
                  {
                    header: '',
                    getValue: () => (
                      <div className="text-main cursor-pointer">Show</div>
                    ),
                  },
                  {
                    header: 'Details',
                    getValue: row => (
                      <div
                        className="text-main cursor-pointer"
                        onClick={() => setSelectedCall(row)}
                      >
                        Details
                      </div>
                    ),
                  },
                ],
              }}
            />
          </div>

          <div className="mt-sm flex justify-between items-center">
            <SearchPanel
              totalItems={pagination?.totalItems}
              page={pagination?.page}
              perPage={10}
              withPaginate
              setPage={setPage}
            />
          </div>
        </div>

        {selectedCall && (
          <div className="w-1/3">
            <CallsTaskDetailsPanel
              call={selectedCall}
              onClose={() => setSelectedCall(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CallsTasksList;
